import { Component, effect, ElementRef, input, output, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { normalizeTagCandidate, splitTagList } from 'utilities';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

const COMMIT_KEYS = new Set(['Enter', ';']);

@Component({
  selector: 'ocp-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrl: './tags-input.component.scss',
  imports: [TranslatePipe, ThemedIconComponent],
})
export class TagsInputComponent {
  private static nextId = 0;

  readonly instanceId = `tags-input-${TagsInputComponent.nextId++}`;
  readonly value = input<string[]>([]);
  readonly placeholder = input('');
  readonly valueChange = output<string[]>();

  protected readonly inputValue = signal('');
  protected readonly isFocused = signal(false);

  // Index of the chip currently being edited inline (click-to-edit), or null when none is.
  protected readonly editingIndex = signal<number | null>(null);
  protected readonly editValue = signal('');

  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('tagInput');
  // Only the chip at `editingIndex` renders this ref at any given time.
  private readonly editInputRef = viewChild<ElementRef<HTMLInputElement>>('editInput');

  constructor() {
    // Focus the edit input and place the cursor at the end as soon as it renders.
    effect(() => {
      if (this.editingIndex() === null) return;
      const inputEl = this.editInputRef()?.nativeElement;
      inputEl?.focus();
      const end = inputEl?.value.length ?? 0;
      inputEl?.setSelectionRange(end, end);
    });
  }

  protected onInput(value: string): void {
    this.inputValue.set(value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (COMMIT_KEYS.has(event.key)) {
      event.preventDefault();
      this.commitPending();
      return;
    }
    if (event.key === 'Backspace' && this.inputValue().length === 0 && this.value().length > 0) {
      this.removeTag(this.value().length - 1);
    }
  }

  protected onFocus(): void {
    this.isFocused.set(true);
  }

  protected onBlur(): void {
    this.commitPending();
    this.isFocused.set(false);
  }

  protected onEscape(): void {
    this.inputValue.set('');
    this.inputRef()?.nativeElement.blur();
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const existing = this.value();
    const newTags = splitTagList(text).filter(
      (tag, i, all) => !existing.includes(tag) && all.indexOf(tag) === i
    );

    if (newTags.length === 0) return;
    this.valueChange.emit([...existing, ...newTags]);
  }

  protected removeTag(index: number): void {
    this.valueChange.emit(this.value().filter((_, i) => i !== index));

    // Keep a pending edit pointed at the right chip once indexes shift.
    const editing = this.editingIndex();
    if (editing === index) {
      this.cancelEdit();
    } else if (editing !== null && editing > index) {
      this.editingIndex.set(editing - 1);
    }
  }

  // Enters inline edit for the chip at `index`. Commit key or blur confirms; Escape cancels.
  protected startEditingChip(index: number): void {
    this.editingIndex.set(index);
    this.editValue.set(this.value()[index] ?? '');
  }

  protected onEditInput(value: string): void {
    this.editValue.set(value);
  }

  protected onEditKeydown(event: KeyboardEvent, index: number): void {
    if (COMMIT_KEYS.has(event.key)) {
      event.preventDefault();
      this.commitEdit(index);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  protected onEditBlur(index: number): void {
    this.commitEdit(index);
  }

  private commitPending(): void {
    const candidate = normalizeTagCandidate(this.inputValue());
    this.inputValue.set('');
    if (!candidate || this.value().includes(candidate)) return;

    this.valueChange.emit([...this.value(), candidate]);
  }

  // Commits a non-empty, non-duplicate tag edit. Unchanged/empty/duplicate just exits edit mode.
  private commitEdit(index: number): void {
    const newValue = normalizeTagCandidate(this.editValue());
    const tags = this.value();
    const original = tags[index];

    const isDuplicateElsewhere = tags.some((tag, i) => i !== index && tag === newValue);
    if (newValue === original || newValue === '' || isDuplicateElsewhere) {
      this.cancelEdit();
      return;
    }

    this.valueChange.emit(tags.map((tag, i) => (i === index ? newValue : tag)));
    this.cancelEdit();
  }

  private cancelEdit(): void {
    this.editingIndex.set(null);
    this.editValue.set('');
  }
}
